import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { SiteFooter } from "@/components/SiteFooter";
import { BaseballCursor } from "@/components/BaseballCursor";

const COLUMNS = ["team", "div", "pos", "bats", "throws", "age"];
const COLUMN_LABELS = { team: "Team", div: "Div", pos: "Pos", bats: "Bats", throws: "Thr", age: "Age" };
const EMOJI = { green: "🟩", gold: "🟨", gray: "⬜" };
const MAX_GUESSES = 9;

const TILE_STYLES = `
@keyframes lc-flip {
  0% { transform: rotateX(-90deg); opacity: 0.35; }
  100% { transform: rotateX(0deg); opacity: 1; }
}
.lc-tile { animation: lc-flip 0.5s cubic-bezier(0.21, 0.6, 0.35, 1) both; backface-visibility: hidden; }
@keyframes lc-pop {
  0% { transform: translateY(8px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}
.lc-pop { animation: lc-pop 0.45s cubic-bezier(0.21, 0.6, 0.35, 1) both; }
.lc-press { transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.2s ease, color 0.2s ease; }
.lc-press:active { transform: translate(3px, 3px); box-shadow: 1px 1px 0 0 currentColor; }
`;

const normalize = (s) =>
  (s || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.,]/g, "")
    .toLowerCase();

const stateKey = (date) => `bsc-lineup-card-${date}`;
const STATS_KEY = "bsc-lineup-card-stats";

const loadJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};
const saveJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode */
  }
};

const defaultStats = { played: 0, wins: 0, streak: 0, maxStreak: 0, dist: {} };

function tileClasses(color) {
  if (color === "green") return "bg-[#2E7D46] text-white";
  if (color === "gold") return "bg-[#C9A227] text-white";
  return "bg-[#A0AAB5] text-white";
}

function Tile({ color, label, delay, testId, children }) {
  return (
    <div
      className={`lc-tile flex flex-col items-center justify-center gap-1 h-14 sm:h-16 ${tileClasses(color)}`}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={testId}
    >
      <span className="font-mono text-[8px] tracking-[0.16em] uppercase opacity-75 leading-none">{label}</span>
      <span className="font-mono text-[11px] sm:text-sm font-bold uppercase leading-none text-center">{children}</span>
    </div>
  );
}

function GuessRow({ entry, index }) {
  const { guess, feedback } = entry;
  const divContent =
    guess.league && guess.division ? (
      <span className="flex flex-col items-center leading-none gap-0.5">
        <span>{guess.league}</span>
        <span className="text-[9px] sm:text-[10px] font-semibold">{guess.division}</span>
      </span>
    ) : (
      "—"
    );
  return (
    <div className="lc-pop border border-brand-black bg-brand-rule shadow-soft" data-testid={`dn-guess-row-${index}`}>
      <div className="flex sm:hidden items-center justify-between bg-white border-b border-brand-rule px-3 py-2">
        <span className="font-serif font-bold text-sm text-brand-black truncate" data-testid={`dn-guess-name-${index}`}>
          {guess.name}
        </span>
        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-brand-muted">Inn {index + 1}</span>
      </div>
      <div className="grid grid-cols-6 sm:grid-cols-[minmax(0,1.8fr)_repeat(6,minmax(0,1fr))] gap-px">
        <div
          className="lc-tile hidden sm:flex items-center gap-2.5 bg-white px-3 h-16 min-w-0"
          style={{ animationDelay: "0ms" }}
        >
          <span className="font-mono text-[10px] font-bold text-brand-red shrink-0">{index + 1}</span>
          <span className="font-serif font-bold text-sm text-brand-black leading-tight truncate">{guess.name}</span>
        </div>
        <Tile color={feedback.team} label={COLUMN_LABELS.team} delay={110} testId={`dn-tile-team-${index}`}>
          {guess.team_abbr}
        </Tile>
        <Tile color={feedback.div} label={COLUMN_LABELS.div} delay={220} testId={`dn-tile-div-${index}`}>
          {divContent}
        </Tile>
        <Tile color={feedback.pos} label={COLUMN_LABELS.pos} delay={330} testId={`dn-tile-pos-${index}`}>
          {guess.position}
        </Tile>
        <Tile color={feedback.bats} label={COLUMN_LABELS.bats} delay={440} testId={`dn-tile-bats-${index}`}>
          {guess.bats}
        </Tile>
        <Tile color={feedback.throws} label={COLUMN_LABELS.throws} delay={550} testId={`dn-tile-throws-${index}`}>
          {guess.throws}
        </Tile>
        <Tile color={feedback.age} label={COLUMN_LABELS.age} delay={660} testId={`dn-tile-age-${index}`}>
          <span className="flex items-center gap-0.5">
            {guess.age ?? "?"}
            {feedback.age !== "green" && feedback.age_hint ? (
              <span className="text-[9px]">{feedback.age_hint === "higher" ? "▲" : "▼"}</span>
            ) : null}
          </span>
        </Tile>
      </div>
    </div>
  );
}

function EmptyRow({ inning }) {
  return (
    <div
      className={`grid grid-cols-[minmax(0,1.8fr)_repeat(6,minmax(0,1fr))] gap-1 px-1 py-0.5 ${
        inning % 2 === 0 ? "bg-brand-surface/70" : "bg-transparent"
      }`}
    >
      <div className="flex items-center gap-2 border border-dashed border-brand-black/20 px-3 h-9 sm:h-10">
        <span className="font-mono text-[9px] font-bold text-brand-red">{inning}</span>
        <span className="font-mono text-[8px] sm:text-[9px] tracking-[0.22em] uppercase text-brand-muted-soft">Inning</span>
      </div>
      {COLUMNS.map((c) => (
        <div key={c} className="border border-dashed border-brand-black/20 h-9 sm:h-10" />
      ))}
    </div>
  );
}

function StatCell({ label, value, testId }) {
  return (
    <div className="flex flex-col items-center bg-brand-paper px-2 py-3.5" data-testid={testId}>
      <span className="font-serif text-2xl font-black text-brand-black leading-none">{value}</span>
      <span className="mt-2 font-mono text-[8px] uppercase tracking-[0.2em] text-brand-muted">{label}</span>
    </div>
  );
}

function SignupModule() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setError("Enter a real email so we have somewhere to send it.");
      return;
    }
    setError("");
    setState("submitting");
    try {
      await api.post("/free-signup", { email: email.trim() });
      setState("done");
    } catch (err) {
      setError(err?.response?.data?.detail ?? "Something went sideways. Try again.");
      setState("idle");
    }
  };

  if (state === "done") {
    return (
      <p className="text-sm font-semibold text-brand-black" data-testid="dn-signup-success">
        You're in. Check your inbox — your first weekly briefing lands Friday.
      </p>
    );
  }
  return (
    <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2" data-testid="dn-signup-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        disabled={state === "submitting"}
        className="flex-1 rounded-none border border-brand-black bg-white px-3 py-3 text-sm text-brand-black placeholder:text-brand-muted-soft focus:outline-none focus:ring-1 focus:ring-brand-black transition-colors disabled:opacity-60"
        data-testid="dn-signup-email-input"
      />
      <button
        type="submit"
        disabled={state === "submitting"}
        className="lc-press bg-brand-ink text-white px-5 py-3 font-semibold text-sm shadow-hard-sm hover:bg-brand-red disabled:opacity-60 whitespace-nowrap"
        data-testid="dn-signup-submit"
      >
        {state === "submitting" ? "Signing up..." : "Sign Up Free"}
      </button>
      {error ? (
        <p className="text-xs text-brand-red font-semibold sm:w-full" role="alert" data-testid="dn-signup-error">
          {error}
        </p>
      ) : null}
    </form>
  );
}

export default function LineupCardPage() {
  const [puzzle, setPuzzle] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [game, setGame] = useState(null);
  const [stats, setStats] = useState(() => loadJSON(STATS_KEY, defaultStats));
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [guessError, setGuessError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api
      .get("/dailynine/today")
      .then((r) => {
        setPuzzle(r.data);
        setGame(
          loadJSON(stateKey(r.data.date), {
            guesses: [],
            status: "playing",
            answer: null,
          }),
        );
      })
      .catch(() => setLoadError("Couldn't load today's card. Try again in a minute."));
  }, []);

  useEffect(() => {
    if (puzzle && game) saveJSON(stateKey(puzzle.date), game);
  }, [puzzle, game]);

  const guessedIds = useMemo(
    () => new Set((game?.guesses || []).map((g) => g.guess.mlb_id)),
    [game],
  );

  const suggestions = useMemo(() => {
    if (!puzzle || !query.trim()) return [];
    const q = normalize(query);
    return puzzle.players
      .filter((p) => normalize(p.name).includes(q) && !guessedIds.has(p.mlb_id))
      .slice(0, 8);
  }, [puzzle, query, guessedIds]);

  const recordFinish = (won, guessCount) => {
    setStats((prev) => {
      const next = {
        played: prev.played + 1,
        wins: prev.wins + (won ? 1 : 0),
        streak: won ? prev.streak + 1 : 0,
        maxStreak: Math.max(prev.maxStreak, won ? prev.streak + 1 : 0),
        dist: { ...prev.dist, ...(won ? { [guessCount]: (prev.dist[guessCount] || 0) + 1 } : {}) },
      };
      saveJSON(STATS_KEY, next);
      return next;
    });
  };

  const submitGuess = async (player) => {
    if (!puzzle || !game || game.status !== "playing" || submitting) return;
    setSubmitting(true);
    setGuessError("");
    setQuery("");
    setOpen(false);
    try {
      const r = await api.post("/dailynine/guess", { date: puzzle.date, mlb_id: player.mlb_id });
      const entry = { guess: r.data.guess, feedback: r.data.feedback };
      const guesses = [...game.guesses, entry];
      if (r.data.correct) {
        setGame({ ...game, guesses, status: "won", answer: r.data.answer });
        recordFinish(true, guesses.length);
        setShowResults(true);
      } else if (guesses.length >= (puzzle.max_guesses || MAX_GUESSES)) {
        let answer = null;
        try {
          const rev = await api.post("/dailynine/reveal", {
            date: puzzle.date,
            guessed_ids: guesses.map((g) => g.guess.mlb_id),
          });
          answer = rev.data.answer;
        } catch {
          /* answer stays hidden */
        }
        setGame({ ...game, guesses, status: "lost", answer });
        recordFinish(false, guesses.length);
        setShowResults(true);
      } else {
        setGame({ ...game, guesses });
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        setGuessError("A new Lineup Card just went live — refresh the page to play today's puzzle.");
      } else {
        setGuessError(err?.response?.data?.detail ?? "Guess didn't go through. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const shareText = () => {
    const rows = game.guesses
      .map((g) => COLUMNS.map((c) => EMOJI[g.feedback[c]] || "⬜").join(""))
      .join("\n");
    const score = game.status === "won" ? `${game.guesses.length}/9` : "X/9";
    return `The Lineup Card #${puzzle.puzzle_number} — ${score}\n\n${rows}\n\nPlay at ${window.location.origin}/lineup-card`;
  };

  const onShare = async () => {
    const text = shareText();
    try {
      if (navigator.share) {
        await navigator.share({ text });
        return;
      }
      throw new Error("no-share");
    } catch {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      } catch {
        /* clipboard blocked */
      }
    }
  };

  const finished = game && game.status !== "playing";
  const winPct = stats.played ? Math.round((stats.wins / stats.played) * 100) : 0;

  return (
    <div
      className="bg-brand-paper text-brand-black min-h-screen flex flex-col"
      style={{
        backgroundImage:
          "repeating-linear-gradient(90deg, rgba(8,40,72,0.04) 0 1px, transparent 1px 16px)",
      }}
    >
      <style>{TILE_STYLES}</style>
      <BaseballCursor size={0.75} />
      <Nav />
      <main className="flex-1 px-4 sm:px-6 pt-28 sm:pt-36 pb-24">
        <div className="max-w-2xl mx-auto">
          {/* Masthead */}
          <header className="text-center animate-fade-up">
            <p
              className="font-mono text-[10px] sm:text-[11px] font-bold tracking-[0.3em] uppercase text-brand-red"
              data-testid="dn-eyebrow"
            >
              The Box Score Club Presents
            </p>
            <div className="relative mt-5 bg-brand-black border-2 border-brand-black py-7 sm:py-9 px-4 shadow-card">
              <span aria-hidden="true" className="absolute top-2 left-2 w-1.5 h-1.5 bg-brand-red" />
              <span aria-hidden="true" className="absolute top-2 right-2 w-1.5 h-1.5 bg-brand-red" />
              <span aria-hidden="true" className="absolute bottom-2 left-2 w-1.5 h-1.5 bg-brand-red" />
              <span aria-hidden="true" className="absolute bottom-2 right-2 w-1.5 h-1.5 bg-brand-red" />
              <h1
                className="font-serif font-black text-4xl sm:text-5xl uppercase tracking-tight text-white leading-none"
                data-testid="dn-headline"
              >
                The Lineup Card
              </h1>
              <div aria-hidden="true" className="mx-auto mt-4 w-14 h-1 bg-brand-red" />
              <p className="mt-4 text-sm sm:text-base text-white/75">
                One mystery MLB player. Nine innings' worth of guesses.
              </p>
            </div>
            {puzzle ? (
              <p
                className="mt-4 flex items-center justify-center gap-2.5 font-mono text-[10px] tracking-[0.24em] uppercase text-brand-muted"
                data-testid="dn-puzzle-meta"
              >
                <span aria-hidden="true" className="inline-block w-1.5 h-1.5 bg-brand-red" />
                No. {puzzle.puzzle_number} · {puzzle.date_label}
                <span aria-hidden="true" className="inline-block w-1.5 h-1.5 bg-brand-red" />
              </p>
            ) : null}
          </header>

          {loadError ? (
            <p className="mt-12 text-center text-brand-red font-semibold" data-testid="dn-load-error">
              {loadError}
            </p>
          ) : null}

          {!puzzle && !loadError ? (
            <p className="mt-12 text-center font-mono text-xs tracking-[0.2em] uppercase text-brand-muted animate-pulse-soft">
              Warming up in the bullpen...
            </p>
          ) : null}

          {puzzle && game ? (
            <>
              {/* Input */}
              {!finished ? (
                <div className="mt-10">
                  <p className="flex items-center gap-2 font-mono text-[10px] font-bold tracking-[0.24em] uppercase text-brand-black">
                    <span aria-hidden="true" className="inline-block w-1.5 h-1.5 bg-brand-red animate-pulse-soft" />
                    Now batting · Inning {game.guesses.length + 1}
                  </p>
                  <div className="relative mt-2.5" data-testid="dn-input-wrap">
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    disabled={submitting}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setOpen(true);
                      setGuessError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && suggestions.length > 0) {
                        e.preventDefault();
                        submitGuess(suggestions[0]);
                      }
                    }}
                    placeholder={`Guess ${game.guesses.length + 1} of ${puzzle.max_guesses} — search any MLB player`}
                    className="w-full rounded-none border border-brand-black bg-brand-surface focus:bg-brand-paper px-4 py-4 text-base text-brand-black placeholder:text-brand-muted-soft focus:outline-none focus:ring-1 focus:ring-brand-black transition-colors shadow-hard-sm disabled:opacity-60"
                    data-testid="dn-guess-input"
                    autoComplete="off"
                  />
                  {open && suggestions.length > 0 ? (
                    <ul
                      className="absolute z-20 inset-x-0 top-full mt-1.5 border border-brand-black bg-brand-paper shadow-hard max-h-80 overflow-auto"
                      data-testid="dn-suggestions"
                    >
                      {suggestions.map((p) => (
                        <li key={p.mlb_id}>
                          <button
                            type="button"
                            onClick={() => submitGuess(p)}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left border-l-2 border-l-transparent hover:border-l-brand-red hover:bg-brand-surface transition-colors"
                            data-testid={`dn-suggestion-${p.mlb_id}`}
                          >
                            <span className="font-serif font-bold text-sm text-brand-black truncate">{p.name}</span>
                            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-brand-muted shrink-0">
                              {p.team_abbr} · {p.position}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {guessError ? (
                    <p className="mt-2.5 text-sm text-brand-red font-semibold" role="alert" data-testid="dn-guess-error">
                      {guessError}
                    </p>
                  ) : null}
                  </div>
                </div>
              ) : null}

              {/* Board */}
              <div className="mt-7 border-2 border-brand-black bg-white shadow-card">
                <div aria-hidden="true" className="h-1.5 bg-brand-red" />
                <div className="hidden sm:grid grid-cols-[minmax(0,1.8fr)_repeat(6,minmax(0,1fr))] bg-brand-black">
                  <span className="text-white/80 px-4 py-2.5 font-mono text-[9px] tracking-[0.24em] uppercase">Batter</span>
                  {COLUMNS.map((c) => (
                    <span key={c} className="text-white/80 py-2.5 text-center font-mono text-[9px] tracking-[0.18em] uppercase">
                      {COLUMN_LABELS[c]}
                    </span>
                  ))}
                </div>
                <div className="p-1.5 sm:p-2 space-y-1.5" aria-live="polite" data-testid="dn-board">
                  {game.guesses.map((entry, i) => (
                    <GuessRow key={entry.guess.mlb_id} entry={entry} index={i} />
                  ))}
                  {!finished
                    ? Array.from({ length: puzzle.max_guesses - game.guesses.length }).map((_, i) => (
                        <EmptyRow key={i} inning={game.guesses.length + i + 1} />
                      ))
                    : null}
                </div>
              </div>

              {/* Legend */}
              {!finished ? (
                <div
                  className="mt-6 flex flex-wrap items-center justify-center gap-2"
                  data-testid="dn-legend"
                >
                  <span className="flex items-center gap-2 border border-brand-rule bg-white px-3 py-1.5 font-mono text-[10px] tracking-[0.14em] uppercase text-brand-muted-strong">
                    <span className="inline-block w-3 h-3 bg-[#2E7D46]" /> Exact
                  </span>
                  <span className="flex items-center gap-2 border border-brand-rule bg-white px-3 py-1.5 font-mono text-[10px] tracking-[0.14em] uppercase text-brand-muted-strong">
                    <span className="inline-block w-3 h-3 bg-[#C9A227]" /> Close
                  </span>
                  <span className="flex items-center gap-2 border border-brand-rule bg-white px-3 py-1.5 font-mono text-[10px] tracking-[0.14em] uppercase text-brand-muted-strong">
                    <span className="inline-block w-3 h-3 bg-[#A0AAB5]" /> Miss
                  </span>
                  <span className="flex items-center gap-2 border border-brand-rule bg-white px-3 py-1.5 font-mono text-[10px] tracking-[0.14em] uppercase text-brand-muted">
                    <span className="text-brand-red font-bold">▲</span> older ·
                    <span className="text-brand-red font-bold">▼</span> younger
                  </span>
                </div>
              ) : null}

              {/* Re-open results */}
              {finished && !showResults ? (
                <button
                  type="button"
                  onClick={() => setShowResults(true)}
                  className="lc-press mt-8 w-full border-2 border-brand-black bg-white py-4 font-mono text-xs tracking-[0.24em] uppercase font-bold text-brand-black shadow-hard-sm hover:bg-brand-black hover:text-white"
                  data-testid="dn-view-results"
                >
                  View Final Scorecard
                </button>
              ) : null}

              {/* Finish overlay */}
              {finished && showResults ? (
                <div
                  className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center overflow-y-auto bg-[#082848]/40 backdrop-blur-md px-4 py-8"
                  data-testid="dn-finish-overlay"
                >
                  <div className="lc-pop relative w-full max-w-lg bg-brand-paper border-2 border-brand-black shadow-[8px_8px_0_0_#082848] p-6 sm:p-9">
                    <button
                      type="button"
                      onClick={() => setShowResults(false)}
                      className="absolute top-3 right-3 font-mono text-[9px] tracking-[0.2em] uppercase px-2.5 py-1.5 border border-brand-black text-brand-black hover:bg-brand-black hover:text-white transition-colors"
                      data-testid="dn-finish-close"
                    >
                      Close ✕
                    </button>
                    <div data-testid="dn-finish-card">
                      <p className="font-mono text-[10px] font-bold tracking-[0.28em] uppercase text-brand-muted text-center">
                        Final · No. {puzzle.puzzle_number}
                      </p>
                      <p className="mt-2.5 font-serif font-black text-3xl sm:text-4xl uppercase tracking-tight text-center text-brand-black">
                        {game.status === "won" ? `Got it in ${game.guesses.length}` : "Struck Out"}
                      </p>

                      {game.answer ? (
                        <div className="mt-6 flex items-center gap-4 border border-brand-black bg-white p-4 shadow-soft">
                          <img
                            src={game.answer.headshot}
                            alt={game.answer.name}
                            className="w-20 h-20 rounded-[2px] border-2 border-brand-black object-cover bg-brand-surface"
                            data-testid="dn-answer-headshot"
                          />
                          <div className="min-w-0">
                            <p className="font-mono text-[9px] tracking-[0.22em] uppercase text-brand-red font-bold">
                              Today's Mystery Player
                            </p>
                            <p className="mt-1 font-serif text-xl sm:text-2xl font-black text-brand-black leading-tight" data-testid="dn-answer-name">
                              {game.answer.name}
                            </p>
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-brand-muted-strong">
                              {game.answer.team_name} · {game.answer.position} · Age {game.answer.age}
                            </p>
                          </div>
                        </div>
                      ) : null}

                      <div className="mt-6 grid grid-cols-4 gap-px bg-brand-black border border-brand-black">
                        <StatCell label="Played" value={stats.played} testId="dn-stat-played" />
                        <StatCell label="Win %" value={winPct} testId="dn-stat-winpct" />
                        <StatCell label="Streak" value={stats.streak} testId="dn-stat-streak" />
                        <StatCell label="Best" value={stats.maxStreak} testId="dn-stat-maxstreak" />
                      </div>

                      <button
                        type="button"
                        onClick={onShare}
                        className="lc-press mt-6 w-full bg-brand-black text-white px-6 py-4 font-semibold text-base shadow-[4px_4px_0_0_#C62C29] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0_0_#C62C29]"
                        style={{ transition: "transform 0.15s ease, box-shadow 0.15s ease" }}
                        data-testid="dn-share-button"
                      >
                        {copied ? "Copied to clipboard ✓" : "Share your scorecard"}
                      </button>

                      <div className="mt-8 border-t border-brand-rule pt-6">
                        <p className="font-serif text-lg font-black text-brand-black">
                          Like this? You'll love the morning briefing.
                        </p>
                        <p className="mt-1.5 text-sm text-brand-muted-strong leading-relaxed">
                          Everything that mattered in baseball, in your inbox before your first meeting. Free.
                        </p>
                        <div className="mt-4">
                          <SignupModule />
                        </div>
                      </div>

                      <p className="mt-7 text-center">
                        <Link
                          to="/"
                          className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-muted-strong hover:text-brand-red transition-colors"
                          data-testid="dn-home-link"
                        >
                          ← Back to The Box Score Club
                        </Link>
                      </p>
                      <p className="mt-3 text-center font-mono text-[9px] tracking-[0.2em] uppercase text-brand-muted-soft">
                        New mystery player at midnight ET
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
